import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogClose } from '@angular/material/dialog';
import { FulfillmentStatusType, Parcel } from '../../../../globalTypes/fulfillment.types';
import { MatAnchor } from "@angular/material/button";
import { OrderItem } from '../../../../globalTypes/order.types';
import { MatCheckbox, MatCheckboxModule } from "@angular/material/checkbox";
import { ParcelUnitsComponent } from "../../component/shipping/parcel-units.component";
import { ParcelDimensionsComponent } from "../../component/shipping/parcel-dimensions.component";
import { CancelOrderData } from '../../../../globalTypes/refund.types';

@Component({
  selector: 'msh-cancel-order-dialog',
  imports: [FormsModule, MatDialogClose, MatCheckboxModule, MatAnchor, MatCheckbox, ParcelUnitsComponent, ParcelDimensionsComponent],
  template: `
    <div class=" text-copy min-w-[50vw]">
      <div class=" overflow-auto max-h-[80vh]">
        @if(data.persistingOrderItems.length > 0){
          <div class=" p-8 pb-2">
            <h2 class="text-lg font-bold text-copy">Confirm Refund</h2>
            <p class="text-sm mt-3 text-copy">Are you sure you want to cancel this {{ data.isBuyer ? 'purchase' : 'fulfillment' }}? This action cannot be undone.</p>
          </div>
          @if(!preShippingStatuses.includes(data.fulfillmentStatus)){
            <div class="p-8 pt-0">
              <h3 class="text-md font-bold text-copy mb-4">Items To Be Returned</h3>
              <div class="flex flex-col gap-4">
                @for(item of data.persistingOrderItems; track item.id){
                  <mat-checkbox [checked]="item.selected" (change)="toggleOrderItems(item.id)" class="flex items-center gap-4">
                    <img class="w-16 h-16 object-cover" src="{{item.url || 'ListingPlaceHolder.png'}}" alt="Item Image"/>
                    <div>
                      <div>{{item.name}}</div>
                      <div class="text-sm text-copy">Quantity: {{item.quantity}}</div>
                    </div>
                  </mat-checkbox>
                }
              </div>
            </div>
            @if(data.isBuyer && postArrivalStatuses.includes(data.fulfillmentStatus)){
              <div>
                <!-- ask the customer if the item was faulty or damaged on arrival -->
                <h3 class="text-md font-bold text-copy mb-4 px-8">Condition of Item</h3>
                <div class="flex flex-col gap-4 px-8">
                  <mat-checkbox [(ngModel)]="data.claimedToBeFaulty" class="flex items-center gap-4">
                    I am claiming that the item was faulty, damaged, or not as described on arrival.
                  </mat-checkbox>
                </div>
              </div>
            }
          }
          <div class=" p-8 pt-0">
            <textarea [(ngModel)]="data.message"
              name="message"
              class="border"
              rows="7"
              [required]="messageIsRequired"
              [placeholder]="placeholder || 'Message'"
            ></textarea>
          </div>
          @if(!data.isBuyer){
            <div>
              <h3 class="text-md font-bold text-copy mb-4 px-8">Restock Item?</h3>
              <div class="flex flex-col gap-4 px-8">
                <mat-checkbox [(ngModel)]="data.restockItems" class="flex items-center gap-4">
                  Yes, I want to restock the item(s) from this fulfillment so they can be purchased again. 
                </mat-checkbox>
                <div class="text-xs text-copy">(If unchecked, the item(s) will be marked as unavailable and will not be purchasable again until I manually relist them)</div>
              </div>
            </div>
          }
          @if(data.isBuyer && postArrivalStatuses.includes(data.fulfillmentStatus)){
            <!-- select the dimensions of the parcel needed to send back the item -->
            <div class="p-8 pt-0">
              <h3 class="text-md font-bold text-copy mb-4">Return Shipping</h3>
              <p class="text-sm text-copy mb-4">
                @if(data.claimedToBeFaulty){
                  Since you are claiming that the item was faulty or damaged on arrival, we
                }@else{
                  We
                }
                will provide you with a shipping label to return the item back to the seller. Please provide the dimensions and weight of the parcel you will be sending back so we can generate the correct shipping label.</p>
              <msh-parcel-units class="flex justify-end mr-6 mb-3" [parcel]="parcel" (parcelUpdated)="parcelUpdated()"></msh-parcel-units>
              <msh-parcel-dimensions [parcel]="parcel"  (parcelUpdated)="parcelUpdated()"></msh-parcel-dimensions>
            </div>
          }
          <div class="m-8 mt-0">{{ cancelInformation }}</div>
        }@else {
          {{data.persistingOrderItems.length}}
          <div class=" p-8 pb-2">
            <h2 class="text-lg font-bold text-copy">Cancel & Refund </h2>
          </div>
          <div class="p-8 pt-0 flex items-center gap-4">
            No items available for refund. This likely means that all items in this fulfillment have already been refunded or returned.
          </div>
        }
        
      </div>
      <div class="bg-copy/10 flex justify-between py-4 px-6 gap-3">
          <button mat-flat-button [mat-dialog-close]="false">Close</button>
          <button mat-raised-button color="warn" [disabled]="messageIsRequired && !data.message" [mat-dialog-close]="data">{{cta || 'Cancel Purchase Now'}}</button>
      </div>
    </div>
  `,
  styles: ``
})
export class CancelOrderDialogComponent implements OnInit {
  loading: boolean = true;
  cancelInformation: string = '';
  placeholder: string = 'Message';
  messageIsRequired: boolean = true;
  preShippingStatuses: FulfillmentStatusType[] = ["required","waiting","delinquency","fulfilling"];
  postArrivalStatuses: FulfillmentStatusType[] = ["shipping","arrived","review","delivered"];
  cta = 'Cancel Purchase Now';
  parcel: Parcel = {
    id: '',
    fulfillmentId: '',
    name: '',
    width: 0,
    length: 0,
    height: 0,
    weight: 0,
    distanceUnit: 'in',
    weightUnit: 'oz',
  } as Parcel;
  constructor(
    public dialogRef: MatDialogRef<CancelOrderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CancelOrderData
  ){ }
  async ngOnInit(){
    if(this.data.parcel) {
      this.data.parcel.id = "";
      this.data.parcel.shippingProcessorParcelId = "";
    }
    this.parcel = this.data.parcel || this.parcel;
    if(!this.data.persistingOrderItems || !this.data.persistingOrderItems.length){
      this.cancelInformation = "All items in this fulfillment have already been refunded or returned.";
      this.loading = false;
      return;
    }
    this.placeholder = "Send an explanation for cancelling to the " + (this.data.isBuyer ? "seller" : " buyer");
    if(this.postArrivalStatuses.includes(this.data.fulfillmentStatus)){
      this.cta = this.data.isBuyer ? "Begin Refund Process" : "Refund The Purchase";
      this.cancelInformation = this.data.isBuyer 
        ? "The seller may dispute a refund if they believe they were not at fault." 
        : "Cancel this fulfillment and notify the buyer that they will get a full refund. ";
    }
    switch(this.data.fulfillmentStatus as FulfillmentStatusType){
      case "required":
      case "waiting":
      case "delinquency":
        this.cancelInformation = this.data.isBuyer 
          ? "Your purchase has not shipped yet.  If you cancel now, you will get a full refund." 
          : "Cancel this fulfillment before shipping and notify the buyer that they will get a full refund. ";
        this.cta = this.data.isBuyer ? "Begin Refund Process" : "Refund The Purchase";
        break;
      case "fulfilling":
        this.cancelInformation = this.data.isBuyer 
          ? "Your purchase is being fulfilled.  If you cancel now, you should get a full refund." 
          : "Cancel this fulfillment before shipping and notify the buyer that they will get a full refund. Please add a message below letting the buyer know why you are cancelling.";
        this.cta = this.data.isBuyer ? "Begin Refund Process" : "Refund The Purchase";
        break;
      case "terminated":
        // Order is already cancelled, so just show option to close dialog
        break;
      case "delayed":
      case "incident":
      case "review":
        break;
      case "shipping":
        break;
      case "arrived":
        break;
    }
  }

  parcelUpdated(){
    this.data.parcel = this.parcel;
  }

  toggleOrderItems(itemId: string){
    this.data.selectedOrderItems = this.data.persistingOrderItems.filter(i => i.selected || this.data.selectedOrderItems.some(i => i.id === itemId));
  }
}
